import { CanComponentDeactivate } from './../common/guard/can-deactivate.guard';
import { Subscription } from 'rxjs';
import { PollingStatus } from './../service/polling-status';
import { ApplicationConfigurationService } from './../application-configuration/application-configuration.service';
import { DashboardEventService } from './../dashboard/dashboard-event.service';
import { AppConfig } from './../app.config';
import { OnDestroy, Inject, HostListener } from '@angular/core';
import { HomeSideBarComponent } from './home.sidebar.component';
import { HomeTopBarComponent } from './home.topbar.component';
import { OnInit } from '@angular/core';
import { UserLoginService } from './../authentication/user-login.service';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { LoggedInCallback, CognitoUtil } from '../authentication/cognito.service';
import { Component, AfterViewInit, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { ServiceRegistry } from '../service/service-registry.service';
import { InitializationStep } from '../snapshot/initialization-step';
import * as moment from 'moment';
import { getOperatingDate, UserProfile, convertFromConstantToHuman } from '@liquitics/transaction';

enum MenuOrientation {
    STATIC,
    OVERLAY
};

@Component({
    selector: 'app-home-root',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit, LoggedInCallback, CanComponentDeactivate {

    startupComplete = false;
    startupFailed = false;
    isLoggedOut = false;
    startupMessages: string[] = ['Starting Liquitics Application...'];
    profileFamily: string;
    profileType: string;

    activeTabIndex = 0;

    sidebarActive = false;

    layoutMode: MenuOrientation = MenuOrientation.OVERLAY;

    topbarMenuActive: boolean;

    overlayMenuActive: boolean;

    staticMenuDesktopInactive: boolean;

    staticMenuMobileActive: boolean;

    rotateMenuButton: boolean;

    sidebarClick: boolean;

    topbarItemClick: boolean;

    menuButtonClick: boolean;

    activeTopbarItem: any;

    documentClickListener: Function;

    theme = 'blue';

    criticalAlertMessage: any;

    @ViewChild(HomeTopBarComponent, { static: true }) topBarComponent: HomeTopBarComponent;
    @ViewChild(HomeSideBarComponent, { static: true }) sideBarComponent: HomeSideBarComponent;

    subscription: Subscription;

    @HostListener('window:beforeunload')
    canDeactivate(): Promise<boolean> | boolean {
        const currentUser = this.cognitoUtil.getCurrentUser();
        if (!currentUser) {
            // Post logout, let it navigate to logout page
            return true;
        }
        return false;
    }


    constructor(public renderer: Renderer2,
        public router: Router,
        public route: ActivatedRoute,
        public userService: UserLoginService,
        private serviceRegistry: ServiceRegistry,
        private applicationConfigurationService: ApplicationConfigurationService, // So its initialized...
        private dashboardEventService: DashboardEventService,
        private appConfig: AppConfig,
        public cognitoUtil: CognitoUtil,
        // protected http: HttpClient,
    ) {
    }

    ngOnInit() {
        this.userService.isAuthenticated(this);
    }

    initializeSubscriptions() {
        this.subscription = this.dashboardEventService.restartSubject.subscribe({
            next: (date: Date) => {
                this.startupMessages = [];
                const tenantInfo = this.appConfig.getTenantInfo();
                const now = new Date();
                let triggerReinitialization = false;
                const currentOpDateStr = this.appConfig.getTodayString();
                if (date > now) {
                    this.serviceRegistry.pausePollingServices();
                    // Restore to live mode
                    // const todayLocal = moment().utcOffset(tenantInfo.utcOffset);
                    // const todayString = todayLocal.format('YYYY-MM-DD');
                    // const todayStartUTC = moment.utc(todayString);
                    // this.appConfig.setTodayStartUTC(todayStartUTC);
                    this.appConfig.setToLiveOperatingDate();
                    this.appConfig.asOfDateMode = false;
                    this.appConfig.asOfDatetimeUTC = null;
                    this.serviceRegistry.resumePollingServices();
                    triggerReinitialization = true;
                } else {
                    this.serviceRegistry.pausePollingServices();
                    // Create a UTC date with just a date part which will default the time to start of day
                    // This moment m is created from the raw local date so will carry the local timezone
                    // and will not be adjusted to UTC yet.
                    this.appConfig.asOfDateMode = true;
                    this.appConfig.asOfDatetimeUTC = moment(date).utc();
                    this.serviceRegistry.pausePollingServices(PollingStatus.FIXED_DATE);
                    const newOpDateStr = getOperatingDate(this.appConfig.asOfDatetimeUTC, tenantInfo.utcOffset, tenantInfo.utcEndOfDayHour);
                    triggerReinitialization = (currentOpDateStr !== newOpDateStr);
                    if (triggerReinitialization) {
                        const newOpDatetime = moment.utc(newOpDateStr);
                        this.appConfig.setTodayStartUTC(newOpDatetime);
                    }
                    this.dashboardEventService.timeShiftSubject.next(this.appConfig.asOfDatetimeUTC);
                }
                if (triggerReinitialization) {
                    this.startupComplete = false;
                    this.serviceRegistry.reinitializeServices();
                    setTimeout(() => {
                        this.startupSequence();
                    }, 500);
                }
            }
        });
        this.subscription.add(this.dashboardEventService.profileSubject.subscribe({
            next: (userProfile: UserProfile) => {
                this.profileFamily = userProfile.getProfileFamily();
                this.profileType = userProfile.getProfileType();
                const userProfileDisplayValue = convertFromConstantToHuman(userProfile.getProfileType()) +
                    ' (' + userProfile.getCurrencies().join(',') + ')';
                this.startupMessages.push('Loading Profile: ' + userProfileDisplayValue);
                this.dashboardEventService.restartLive();
            }
        }));
        this.subscription.add(this.dashboardEventService.timeShiftSubject.subscribe({
            next: (asOfDatetime: moment.Moment) => {
                this.appConfig.asOfDateMode = true;
                this.appConfig.asOfDatetimeUTC = asOfDatetime;
            }
        }));
        this.subscription.add(this.dashboardEventService.toggleSideBarSliderSubject.subscribe({
            next: (open: boolean) => {
                if (open) {
                    this.openSidebar();
                } else {
                    this.closeSidebar();
                }
            }
        }));
    }

    isLoggedIn(message: string, isLoggedIn: boolean) {
        if (!isLoggedIn) {
            // const token = this.getParameterByName('code');
            // console.log("****** Code is: " + token + "*****");
            // this.route.queryParams
            //     .debounceTime(500)
            //     .subscribe({next: (params: Params) => {
            //         if (params['code'] !== undefined) {
            //             const authenticationCode = params['code'];
            //             console.log("****** Code is: " + authenticationCode + "*****");
            //         }
            //         this.router.navigate(['/login']);
            //     });
            // return;
            this.isLoggedOut = true;
            this.userService.doCognitoAuthLogin();
            // this.router.navigate(['/login']);
        } else {
            this.cognitoUtil.switchToMemoryStorage();
            this.initializeSubscriptions();
            this.startupSequence();
        }
    }



    startupSequence() {
        this.serviceRegistry.startUpServices()
            .then((steps: InitializationStep[]) => {
                const promises = [];
                steps.forEach((step: InitializationStep, index: number) => {
                    this.startupMessages.push(step.stepName + ': Initializing');
                    promises.push(step.promise);
                    step.progress.subscribe({
                        next: (progressMessage: string) => {
                            this.startupMessages[index] = step.stepName + ': ' + progressMessage;
                        }
                    });
                });
                return Promise.all(promises);
            })
            .then(() => {
                this.startupComplete = true;
                // setTimeout(() => {
                this.topBarComponent.initializeTopBar();
                this.sideBarComponent.initializeSideBar();
                // }, 10);
                if (this.appConfig.asOfDateMode) {
                    this.dashboardEventService.survivalHorizonModeSubject.next('fixed');
                } else {
                    this.serviceRegistry.resumePollingServices();
                    this.dashboardEventService.survivalHorizonModeSubject.next('live');
                }
                this.profileFamily = this.appConfig.activeUserProfile.getProfileFamily();
                this.profileType = this.appConfig.activeUserProfile.getProfileType();
            })
            .catch((err: any) => {
                console.log(err);
                this.startupFailed = true;
                this.startupMessages.push(err);
            })
    }

    ngAfterViewInit() {
        console.log('Initializing Home Component');
        this.documentClickListener = this.renderer.listen('body', 'click', (event) => {
            if (!this.topbarItemClick) {
                this.activeTopbarItem = null;
                this.topbarMenuActive = false;
            }

            if (!this.menuButtonClick && !this.sidebarClick && (this.overlay || !this.isDesktop())) {
                this.sidebarActive = false;
            }

            this.topbarItemClick = false;
            this.sidebarClick = false;
            this.menuButtonClick = false;
        });
    }

    onTabClick(event: Event, index: number, hasExpansion: boolean = true) {
        if (hasExpansion) {
            if (this.activeTabIndex === index) {
                this.sidebarActive = !this.sidebarActive;
            } else {
                this.activeTabIndex = index;
                this.sidebarActive = true;
            }
        } else {
            this.activeTabIndex = index;
            this.sidebarActive = false;
        }
        event.preventDefault();
    }

    toggleSideBar() {
        this.sidebarActive = !this.sidebarActive;
    }

    openSidebar(event?: Event) {
        this.sidebarActive = true;
        if (event) {
            event.preventDefault();
        }
    }
    closeSidebar(event?: Event) {
        this.sidebarActive = false;
        if (event) {
            event.preventDefault();
        }
    }

    onSidebarClick(event: Event) {
        this.sidebarClick = true;
    }

    onTopbarMenuButtonClick(event: Event) {
        this.topbarItemClick = true;
        this.topbarMenuActive = !this.topbarMenuActive;

        event.preventDefault();
    }

    onMenuButtonClick(event: Event, index: number) {
        this.menuButtonClick = true;
        this.rotateMenuButton = !this.rotateMenuButton;
        this.topbarMenuActive = false;
        this.sidebarActive = !this.sidebarActive;

        if (this.layoutMode === MenuOrientation.OVERLAY) {
            this.overlayMenuActive = !this.overlayMenuActive;
        } else {
            if (this.isDesktop()) {
                this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
            } else {
                this.staticMenuMobileActive = !this.staticMenuMobileActive;
            }
        }

        if (this.activeTabIndex < 0) {
            this.activeTabIndex = 0;
        }

        event.preventDefault();
    }

    onTopbarItemClick(event: Event, item) {
        this.topbarItemClick = true;

        if (this.activeTopbarItem === item) {
            this.activeTopbarItem = null;
        } else {
            this.activeTopbarItem = item;
        }
        event.preventDefault();
    }

    onTopbarSearchItemClick(event: Event) {
        this.topbarItemClick = true;
        event.preventDefault();
    }

    get overlay(): boolean {
        return this.layoutMode === MenuOrientation.STATIC;
    }

    changeToStaticMenu() {
        this.layoutMode = MenuOrientation.STATIC;
    }

    changeToOverlayMenu() {
        this.layoutMode = MenuOrientation.OVERLAY;
    }

    showCriticalAlert(message: string) {
        this.criticalAlertMessage = message;
    }

    hideCriticalAlert() {
        this.criticalAlertMessage = [];
    }

    isDesktop() {
        return window.innerWidth > 1024;
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.documentClickListener) {
            this.documentClickListener();
        }
    }
}
